import { useState, useEffect } from "react";
import { X, Loader2, Settings, Cog, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";
import { MCPConfig, ActivateToolsData } from "../../services/mcpConfig.service";

interface ToolsManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: MCPConfig | null;
  onSync: (configId: string) => Promise<void>;
  onActivateTools: (data: ActivateToolsData) => Promise<void>;
  isSyncing: boolean;
  isActivating: boolean;
}

export default function ToolsManagementModal({
  isOpen,
  onClose,
  config,
  onSync,
  onActivateTools,
  isSyncing,
  isActivating,
}: ToolsManagementModalProps) {
  const [toolStates, setToolStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (config && config.tools) {
      const initialStates: Record<string, boolean> = {};
      config.tools.forEach((tool) => {
        initialStates[tool.name] = tool.is_active;
      });
      setToolStates(initialStates);
    }
  }, [config]);

  const handleToolToggle = (toolName: string) => {
    setToolStates((prev) => ({
      ...prev,
      [toolName]: !prev[toolName],
    }));
  };

  const handleSync = async () => {
    if (!config) return;
    try {
      await onSync(config._id);
      toast.success("Tools synced successfully!");
    } catch (error) {
      toast.error("Failed to sync tools");
    }
  };

  const handleSaveChanges = async () => {
    if (!config) return;

    const toolsToUpdate = Object.entries(toolStates).map(
      ([name, is_active]) => ({
        name,
        is_active,
      })
    );

    const data: ActivateToolsData = {
      mcp_id: config._id,
      tools: toolsToUpdate,
    };

    try {
      await onActivateTools(data);
      toast.success("Tool settings updated successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to update tool settings");
    }
  };

  const hasChanges = () => {
    if (!config || !config.tools) return false;

    return config.tools.some((tool) => {
      return toolStates[tool.name] !== tool.is_active;
    });
  };

  if (!isOpen || !config) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center">
              <Cog className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Manage Tools
              </h2>
              <p className="text-sm text-gray-500">{config.mcp_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isSyncing || isActivating}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {/* Sync Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Sync Tools
                </h3>
              </div>
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Syncing...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Sync Tools</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Sync to fetch the latest available tools from this MCP server.
            </p>
            {config.last_sync_at && (
              <p className="text-xs text-gray-500">
                Last synced: {new Date(config.last_sync_at).toLocaleString()}
              </p>
            )}
          </div>

          {/* Tools List */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Available Tools
              </h3>
            </div>

            {config.tools && config.tools.length > 0 ? (
              <div className="space-y-3">
                {config.tools.map((tool) => (
                  <div
                    key={tool.name}
                    className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center h-5">
                      <input
                        id={`tool-${tool.name}`}
                        type="checkbox"
                        checked={toolStates[tool.name] || false}
                        onChange={() => handleToolToggle(tool.name)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        disabled={isActivating}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label
                        htmlFor={`tool-${tool.name}`}
                        className="text-sm font-medium text-gray-900 cursor-pointer"
                      >
                        {tool.name}
                      </label>
                      <p className="text-xs text-gray-600 mt-1">
                        {tool.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            toolStates[tool.name]
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {toolStates[tool.name] ? "Active" : "Inactive"}
                        </span>
                        {tool.server_name && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {tool.server_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Cog className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No tools available
                </h3>
                <p className="text-gray-500 mb-4">
                  Sync this configuration to fetch available tools from the MCP
                  server.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {config.tools && config.tools.length > 0 && (
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white z-10">
            <button
              type="button"
              onClick={onClose}
              disabled={isActivating}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={isActivating || !hasChanges()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isActivating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Changes</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
